import React, {
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  ComponentLevelNotification,
  ProgressCircular,
  SelectNative,
  SelectNativeOption,
} from '@americanexpress/dls-react';
import { useFetchye } from 'fetchye';

import styles from './RepositoryAndDocTypeSelector.css';

function RepositoryAndDocTypeSelector(props) {
  const {
    inboundURL,
    repoConfigId,
    guid,
    icaasMarket,
    icaasFlowType,
    repositoryView,
    onResponseReset,
    submitResponse,
    handleDocTypeRes,
    scrollIntoSelect,
    onSelectionChange,
  } = props;

  const intl = useIntl();
  const [repoId, setRepoId] = useState('');
  const [selectedDocType, dispatchSelectedDocType] = useReducer(
    (state, action) => (action.type === 'set' ? action.value : ''),
    ''
  );

  const selectedItem = useRef(null);
  const {
    data: repoData,
    error: repoDataError,
    isLoading: repoIsLoading,
    run: getRepoConfig,
  } = useFetchye(
    `${inboundURL}/servicing/v1/communications/docrepositories/${repoConfigId}`,
    {
      credentials: 'include',
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'icaas-guid': `${guid}`,
        'icaas-market': `${icaasMarket}`,
        'icaas-flow-type': `${icaasFlowType}`,
      },
    }
  );

  useEffect(() => {
    if (selectedDocType === '' && scrollIntoSelect !== null && selectedItem.current) {
      selectedItem.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [scrollIntoSelect, selectedDocType]);

  useEffect(() => {
    getRepoConfig();
  }, []);

  useEffect(() => {
    if (submitResponse !== null && submitResponse.status.statusCode === '60200') {
      dispatchSelectedDocType({ type: 'reset' });
    }
  }, [submitResponse]);

  // Use API data only when it has the expected collection shape.
  const hasRepoBody = repoData && Array.isArray(repoData.body);
  const repoBody = hasRepoBody ? repoData.body : [];

  const hasError = () => repoDataError != null
    || (repoData && repoData.status !== undefined && repoData.status !== 200);
  if (repoIsLoading) {
    return (
      <div data-testid="repoIsLoading" className={styles['dsc-center']}>
        <ProgressCircular size="lg" />
      </div>
    );
  }

  if (hasError()) {
    return <div data-testid="repoDataError" className="flex flex-align-center flex-justify-center pad-2-tb">Error when loading page, please refresh.</div>;
  }

  const onRepoChange = (event) => {
    if (onResponseReset) onResponseReset();
    const newRepoId = event.target.value;
    setRepoId(newRepoId);
    dispatchSelectedDocType({ type: 'reset' });
    if (onSelectionChange) {
      onSelectionChange({ repoId: newRepoId, docType: '' });
    }
  };

  const onDocTypeChange = (event) => {
    if (onResponseReset) onResponseReset();
    let intentName = 'no_kyc';
    if (hasRepoBody) {
      // Find the selected repo based on repoId
      // (for allHorizontal) or icaasMarket (for onlyDocument)
      const repoKey = repositoryView === 'onlyDocument' ? icaasMarket : repoId;
      const getRepository = (key) => repoData.body.find(
        (items) => items.key === key
          && items.flowType === icaasFlowType) || repoData.body.find((items) => items.key === key);
      const matchedRepo = getRepository(repoKey, icaasFlowType);
      if (matchedRepo && matchedRepo.docTypes) {
        const selectedDocTypeObj = matchedRepo.docTypes.find(
          (docType) => docType.key === event.target.value);
        if (selectedDocTypeObj && selectedDocTypeObj.intentName) {
          intentName = selectedDocTypeObj.intentName;
        }
      }
    }
    if (handleDocTypeRes) handleDocTypeRes(event.target.value, intentName);
    dispatchSelectedDocType({ type: 'set', value: event.target.value });
    if (onSelectionChange) {
      onSelectionChange({ repoId, docType: event.target.value });
    }
  };

  const getRepoOptions = () => repoBody.map((obj) => (
    <SelectNativeOption aria-label={obj.repoNm} key={obj.key} value={obj.id}>
      {obj.repoNm}
    </SelectNativeOption>
  ));

  const getDocTypeOptions = (repoIdKey) => {
    const matchedRepo = repoBody.find(
      (items) => items.key === repoIdKey && items.flowType === icaasFlowType
    ) || repoBody.find((items) => items.key === repoIdKey);

    if (!matchedRepo || !matchedRepo.docTypes) return null;

    return matchedRepo.docTypes.map((docType) => (
      <SelectNativeOption
        aria-label={docType.label}
        key={`option-repo-${docType.key}`}
        value={docType.key}
      >
        {docType.label}
      </SelectNativeOption>
    ));
  };

  const renderSelects = () => {
    switch (repositoryView) {
      case 'onlyDocument':
        return (
          <div data-testid="onlyDocumentView">
            <div>
              <SelectNative
                id="docTypeSelect"
                aria-labelledby="docTypeLabel"
                label={intl.formatMessage({ id: 'docType' })}
                className="width-full"
                onChange={onDocTypeChange}
                value={selectedDocType}
                ref={selectedItem}
                data-testid="docTypeSelect"
                status={selectedDocType === '' && scrollIntoSelect !== null ? 'error' : 'default'}
              >
                <SelectNativeOption key="default-doc" value="">
                  {intl.formatMessage({ id: 'selectOne' })}
                </SelectNativeOption>
                {getDocTypeOptions(icaasMarket)}
              </SelectNative>
            </div>
            {selectedDocType === '' && scrollIntoSelect !== null && (
              <div className={styles['dsc-notification']} aria-live="assertive">
                <ComponentLevelNotification data-testid="selectCategoryAlert" type="error">
                  <FormattedMessage id="selectCategory" />
                </ComponentLevelNotification>
              </div>
            )}
          </div>
        );
      case 'allHorizontal':
      default:
        return (
          <div data-testid="allHorizontalView" className="flex flex-row-md-up flex-column-sm-down">
            <div className="flex-item-grow">
              <SelectNative
                id="repoSelect"
                className={styles.repoSelectControl}
                label={intl.formatMessage({ id: 'repository' })}
                aria-labelledby="repoLabel"
                onChange={onRepoChange}
                value={repoId}
                data-testid="repoSelect"
              >
                <SelectNativeOption key="default-repo" value="">
                  {intl.formatMessage({ id: 'selectOne' })}
                </SelectNativeOption>
                {getRepoOptions()}
              </SelectNative>
            </div>
            {repoId && (
              <div className="flex-item-grow">
                <SelectNative
                  id="docTypeSelect"
                  className={styles.repoSelectControl}
                  label={intl.formatMessage({ id: 'docType' })}
                  aria-labelledby="docTypeLabel"
                  onChange={onDocTypeChange}
                  value={selectedDocType}
                  data-testid="docTypeSelect"
                >
                  <SelectNativeOption key="default-doc" value="">
                    {intl.formatMessage({ id: 'selectOne' })}
                  </SelectNativeOption>
                  {getDocTypeOptions(repoId)}
                </SelectNative>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div>
      {renderSelects()}
    </div>
  );
}

RepositoryAndDocTypeSelector.defaultProps = {
  inboundURL: '',
  repoConfigId: '',
  guid: '',
  icaasMarket: '',
  icaasFlowType: '',
  repositoryView: '',
  onResponseReset: null,
  submitResponse: null,
  handleDocTypeRes: null,
  scrollIntoSelect: null,
  onSelectionChange: null,
};

RepositoryAndDocTypeSelector.propTypes = {
  inboundURL: PropTypes.string,
  repoConfigId: PropTypes.string,
  guid: PropTypes.string,
  icaasMarket: PropTypes.string,
  icaasFlowType: PropTypes.string,
  repositoryView: PropTypes.string,
  onResponseReset: PropTypes.func,
  submitResponse: PropTypes.shape({
    status: PropTypes.shape({
      statusCode: PropTypes.string,
    }),
  }),
  handleDocTypeRes: PropTypes.func,
  scrollIntoSelect: PropTypes.string,
  onSelectionChange: PropTypes.func,
};

export default RepositoryAndDocTypeSelector;
